import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth } from "@/context/providers/AuthProvider";
import { hasRenderableChatTranscript } from "@/lib/agent/renderableChatMessage";
import { useCreatePageStore } from "@/stores/createPageStore";
import { logger } from "@/utils/logger";
import { AgentMessage, AgentPhase } from "@/types/createAgent";
import { CreateFormData } from "@/components/create/types";
import {
  getGeneratedImageStoragePath,
  getPublicImageUrl,
} from "@/utils/imageUtils";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { parseDesignSpecSnapshot } from "@/lib/agent/designSpecSnapshot";
import type { SessionRenderSnapshots } from "@/services/createAgentSessionService";
import {
  clearStoredAgentSessionId,
  isAgentSessionId,
  isStoredAgentSessionAuthenticated,
  markAgentSessionReset,
  markStoredAgentSessionAuthenticated,
  readStoredAgentSessionId,
  storeAgentSessionId,
  wasAgentSessionReset,
} from "../utils/agentSessionId";
import { createAuthoritativeCreateRestoreTargetKey } from "../utils/urlParamHelpers";

export const AGENT_SESSION_RESTORE_TIMEOUT_MS = 8_000;
export const EXACT_SESSION_RESTORE_RETRY_MESSAGE =
  "We couldn't open that dress right now. Please try again.";

function hasAuthoritativeCreateRestoreInCurrentUrl(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const searchParams = new URL(window.location.href).searchParams;
    return Boolean(
      createAuthoritativeCreateRestoreTargetKey({
        modifyParam: searchParams.get("modify"),
        ticketIdParam: searchParams.get("ticket_id"),
        galleryDesignParam: searchParams.get("gallery_design"),
      })
    );
  } catch {
    return false;
  }
}

function findLatestGeneratedImageUrl(
  messages: unknown,
  side: "front" | "back"
): string | undefined {
  if (!Array.isArray(messages)) return undefined;

  for (const message of [...messages].reverse()) {
    if (!message || typeof message !== "object") continue;
    const candidate = message as Partial<AgentMessage>;
    if (typeof candidate.imageUrl !== "string") continue;
    if (
      candidate.imageSide === side ||
      candidate.content?.toLowerCase().includes(`${side} design image`)
    ) {
      return candidate.imageUrl;
    }
  }

  return undefined;
}

function imageReferencesMatch(
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  const leftValue = left?.trim();
  const rightValue = right?.trim();
  if (!leftValue || !rightValue) return false;
  if (leftValue === rightValue) return true;
  const leftPath = getGeneratedImageStoragePath(leftValue);
  const rightPath = getGeneratedImageStoragePath(rightValue);
  return Boolean(leftPath && rightPath && leftPath === rightPath);
}

/**
 * Hook to manage agent session restoration.
 *
 * Restores an authenticated customer's conversation, in order of authority:
 *   1. an explicit `session_id` deep link from My Dresses,
 *   2. the id in localStorage (this browser's own conversation), and
 *   3. failing that, their most recent non-empty session resolved server-side,
 *      so a cleared/absent local id does not strand them next to their own
 *      live chat.
 *
 * It deliberately does NOT create a session. A row minted at page load is a row
 * with no conversation in it, and the id itself is minted by the client at
 * first send (see utils/agentSessionId), so there is nothing to bootstrap here.
 */
export function useAgentSession(
  enabled: boolean = true,
  authoritativeRestoreTargetKey: string | null = null,
  requestedSessionId: string | null = null,
  freshStartRequested: boolean = false
) {
  const { session, loading: authLoading, isAnonymous } = useAuth();
  const userId = session?.user?.id;
  const restorationScopeKey = [
    enabled ? "enabled" : "disabled",
    authoritativeRestoreTargetKey ?? "ordinary-create",
    requestedSessionId ?? "no-requested-session",
    freshStartRequested ? "fresh-start" : "restore-allowed",
    authLoading ? "auth-loading" : "auth-ready",
    isAnonymous ? "anonymous" : "account",
    userId ?? "signed-out",
  ].join("|");
  // Own the fresh-entry surface until both identity and the customer's saved
  // conversation have a definitive answer. Without this barrier, an ordinary
  // reload briefly rendered an actionable anonymous entry while auth and the
  // stored/latest-session request were still in flight.
  const [restorationPending, setRestorationPending] = useState(
    () =>
      !freshStartRequested &&
      (Boolean(authLoading) || Boolean(userId) || Boolean(requestedSessionId))
  );
  const restorationPendingRef = useRef(restorationPending);
  const previousRestorationScopeRef = useRef(restorationScopeKey);
  const setSessionId = useCreatePageStore(state => state.setSessionId);
  const restoreMessages = useCreatePageStore(state => state.restoreMessages);
  const setPendingComposerSeed = useCreatePageStore(
    state => state.setPendingComposerSeed
  );
  const setPhase = useCreatePageStore(state => state.setPhase);
  const setAgentError = useCreatePageStore(state => state.setAgentError);
  const setExactSessionRestoreError = useCreatePageStore(
    state => state.setExactSessionRestoreError
  );
  const updateFormData = useCreatePageStore(state => state.updateFormData);
  const setAgentBrainState = useCreatePageStore(
    state => state.setAgentBrainState
  );
  const restoreImageState = useCreatePageStore(
    state => state.restoreImageState
  );
  const setSessionVersions = useCreatePageStore(
    state => state.setSessionVersions
  );
  const resetAll = useCreatePageStore(state => state.resetAll);
  // Each URL target/session-state change owns a distinct restoration
  // generation. A target can become authoritative while a previous session
  // request is in flight; only the still-current, unsuppressed generation may
  // apply its response.
  const restoreGuardRef = useRef({
    generation: 0,
    authoritativeRestoreTargetKey,
    requestedSessionId,
  });
  const initializingGenerationRef = useRef<number | null>(null);
  // The failed-adoption query marker remains visible briefly so both the
  // session and form restoration effects can observe it. Handle it only once
  // per mount: a new conversation id minted during that cleanup window belongs
  // to the signed-in account and must not be discarded as anonymous work.
  const failedAdoptionHandledRef = useRef(false);
  // The server-side fallback is a once-per-mount courtesy, never a loop. It is
  // NOT what protects Start Over — a ref dies with the mount, and Start Over
  // navigates. The durable reset marker in localStorage does that job.
  const hasAttemptedServerFallbackRef = useRef(false);
  const freshStartHandledRef = useRef(false);
  const previousRequestedProjectBoundaryRef = useRef<string | null>(null);
  const requestedProjectBoundary = freshStartRequested
    ? "fresh-start"
    : requestedSessionId
      ? `session:${requestedSessionId}`
      : null;
  const advanceProjectActionEpoch = useCreatePageStore(
    state => state.advanceProjectActionEpoch
  );

  // An exact-session navigation and Start Over both replace the project before
  // their passive restore/reset work runs. Retire outstanding render promises
  // in the layout phase so they cannot land during that gap.
  useLayoutEffect(() => {
    if (!requestedProjectBoundary) {
      previousRequestedProjectBoundaryRef.current = null;
      return;
    }
    if (
      previousRequestedProjectBoundaryRef.current === requestedProjectBoundary
    ) {
      return;
    }

    previousRequestedProjectBoundaryRef.current = requestedProjectBoundary;
    advanceProjectActionEpoch();
  }, [advanceProjectActionEpoch, requestedProjectBoundary]);

  // Auth can change without remounting this hook (for example, an in-place
  // session revalidation). Re-arm before paint for every new identity scope so
  // the anonymous entry cannot become actionable while that scope restores.
  // Agent message/session versions are deliberately excluded: ordinary chat
  // updates must never put the page back behind the startup shell.
  useLayoutEffect(() => {
    if (previousRestorationScopeRef.current === restorationScopeKey) return;
    previousRestorationScopeRef.current = restorationScopeKey;

    if (
      enabled &&
      !freshStartRequested &&
      !authoritativeRestoreTargetKey &&
      (authLoading || Boolean(userId) || Boolean(requestedSessionId))
    ) {
      if (!restorationPendingRef.current) {
        restorationPendingRef.current = true;
        setRestorationPending(true);
      }
    }
  }, [
    authLoading,
    authoritativeRestoreTargetKey,
    enabled,
    freshStartRequested,
    requestedSessionId,
    restorationScopeKey,
    userId,
  ]);

  // Invalidate an older request during the layout phase. Waiting for the
  // passive-effect cleanup leaves a small post-commit window where its promise
  // can resolve after the explicit target has already cleared the store.
  useLayoutEffect(() => {
    restoreGuardRef.current = {
      generation: restoreGuardRef.current.generation + 1,
      authoritativeRestoreTargetKey,
      requestedSessionId,
    };
    setExactSessionRestoreError(null);
    if (freshStartRequested && !freshStartHandledRef.current) {
      freshStartHandledRef.current = true;
      const currentStore = useCreatePageStore.getState();
      currentStore.abortActiveRequest();
      markAgentSessionReset();
      FormPersistenceService.clearCanonicalState();
      resetAll();

      // Consume the one-shot intent. The reset marker keeps automatic restore
      // suppressed after cleanup, while the prior project remains saved in My
      // Dresses and can still be opened through its exact session URL.
      if (typeof window !== "undefined") {
        const freshUrl = new URL(window.location.href);
        freshUrl.searchParams.delete("new");
        window.history.replaceState(
          window.history.state,
          "",
          `${freshUrl.pathname}${freshUrl.search}${freshUrl.hash}`
        );
      }
    } else if (authoritativeRestoreTargetKey) {
      clearStoredAgentSessionId();
      const liveSessionId = useCreatePageStore.getState().agent.sessionId;
      if (liveSessionId) {
        setSessionId(null);
      }
    }
  }, [
    enabled,
    userId,
    authLoading,
    freshStartRequested,
    authoritativeRestoreTargetKey,
    requestedSessionId,
    resetAll,
    setExactSessionRestoreError,
    setSessionId,
  ]);

  useEffect(() => {
    const restoreGeneration = restoreGuardRef.current.generation;
    let disposed = false;
    const activeRestoreCancels = new Set<() => void>();
    const settleRestoration = () => {
      if (
        disposed ||
        restoreGuardRef.current.generation !== restoreGeneration
      ) {
        return;
      }
      if (!restorationPendingRef.current) return;
      restorationPendingRef.current = false;
      setRestorationPending(false);
    };
    const canApplyRestoredSession = () =>
      !disposed &&
      restoreGuardRef.current.generation === restoreGeneration &&
      restoreGuardRef.current.authoritativeRestoreTargetKey === null &&
      restoreGuardRef.current.requestedSessionId === requestedSessionId &&
      !hasAuthoritativeCreateRestoreInCurrentUrl();

    /** Applies one complete session snapshot without mixing project state. */
    const applyRestoredSession = (
      data: {
        sessionId: string;
        messages?: unknown;
        phase?: AgentPhase;
        formState?: unknown;
        renderSnapshots?: SessionRenderSnapshots;
        agentState?: unknown;
        ticketId?: string | null;
        sessionVersion?: number;
        messageVersion?: number;
      },
      options: { allowSessionSwitch?: boolean } = {}
    ): boolean => {
      if (!canApplyRestoredSession()) {
        logger.debug(
          "Skipped agent-session restore because an explicit create target is authoritative",
          {
            component: "useAgentSession",
            metadata: { authoritativeRestoreTargetKey },
          }
        );
        return false;
      }

      const {
        sessionId,
        messages,
        phase,
        formState,
        renderSnapshots,
        agentState,
        ticketId,
        sessionVersion,
        messageVersion,
      } = data;
      const restoredMessages: AgentMessage[] = Array.isArray(messages)
        ? messages.map((msg: AgentMessage) => ({
            ...msg,
            timestamp:
              msg.timestamp instanceof Date
                ? msg.timestamp
                : new Date(msg.timestamp),
          }))
        : [];

      // The customer may have started typing while the restore was in flight,
      // which mints a live conversation id. That conversation wins: adopting a
      // restored id on top of it would move a half-sent turn onto another row.
      // An explicit, already-authorized My Dresses link is the one exception:
      // after its ownership read succeeds it deliberately replaces the canvas.
      const currentStore = useCreatePageStore.getState();
      const liveSessionId = currentStore.agent.sessionId;
      if (
        liveSessionId &&
        liveSessionId !== sessionId &&
        !options.allowSessionSwitch
      ) {
        logger.info(
          "Skipped restore because a live chat session already exists",
          {
            component: "useAgentSession",
            metadata: { liveSessionId, restoredSessionId: sessionId },
          }
        );
        return false;
      }

      // A restore can begin from the durable browser id and finish after the
      // customer has already sent a new turn on that SAME id. The id equality
      // alone does not make that response safe: it is a snapshot from before
      // the optimistic user message (and often before the server's reply), so
      // applying it here would erase the live transcript and roll version
      // metadata back to zero. Any in-memory conversation on an ordinary
      // startup restore belongs to this tab and wins. Exact My Dresses links
      // remain authoritative via allowSessionSwitch.
      if (
        liveSessionId === sessionId &&
        !options.allowSessionSwitch &&
        currentStore.agent.conversation.messages.length > 0
      ) {
        logger.info(
          "Skipped restore because live chat activity started while it was loading",
          {
            component: "useAgentSession",
            metadata: { sessionId },
          }
        );
        return false;
      }

      const localSessionId = liveSessionId ?? readStoredAgentSessionId();
      const hasRestoreConflict =
        localSessionId === sessionId &&
        typeof sessionVersion === "number" &&
        currentStore.agent.sessionVersion > sessionVersion;

      if (hasRestoreConflict) {
        if (!liveSessionId) {
          setSessionId(sessionId);
          storeAgentSessionId(sessionId);
        }
        setAgentError(
          "restore_conflict: This browser has a newer draft. Choose whether to keep it or load the version saved to your account."
        );
        logger.warn("restore_conflict: local session newer than server", {
          component: "useAgentSession",
          metadata: {
            sessionId,
            localSessionVersion: currentStore.agent.sessionVersion,
            serverSessionVersion: sessionVersion,
          },
        });
        return false;
      }

      // A session snapshot is a replacement boundary, not a partial patch.
      // This is essential for empty-conversation duplicates and cross-project
      // navigation: omitted fields must become empty instead of inheriting the
      // previously open dress's chat, form, images, or planner state.
      currentStore.abortActiveRequest();
      resetAll();
      setSessionId(sessionId);
      storeAgentSessionId(sessionId);
      restoreMessages(restoredMessages);

      logger.info("Agent session and conversation history restored", {
        component: "useAgentSession",
        metadata: {
          sessionId,
          messageCount: restoredMessages.length,
          phase,
        },
      });

      // Restore phase if provided
      if (phase) {
        setPhase(phase);
      }

      // Rehydrate the planner brain state (design brief, active
      // concept, action plan) persisted alongside the session so
      // the agent keeps its context across reloads.
      if (agentState && typeof agentState === "object") {
        setAgentBrainState(
          agentState as Parameters<typeof setAgentBrainState>[0]
        );
      }

      // Restore reconciled form and image state from the clean session canvas.
      {
        const safeFormState =
          formState && typeof formState === "object"
            ? (formState as Partial<CreateFormData> & {
                imageUrl?: string;
                backImageUrl?: string;
              })
            : ({} as Partial<CreateFormData> & {
                imageUrl?: string;
                backImageUrl?: string;
              });
        updateFormData(safeFormState);
        const mergedFormData = {
          ...useCreatePageStore.getState().form.data,
          ...safeFormState,
        } as CreateFormData;
        const restoredFrontImageReference =
          (typeof safeFormState.imageUrl === "string"
            ? safeFormState.imageUrl
            : undefined) ?? findLatestGeneratedImageUrl(messages, "front");
        const restoredBackImageReference =
          (typeof safeFormState.backImageUrl === "string"
            ? safeFormState.backImageUrl
            : undefined) ?? findLatestGeneratedImageUrl(messages, "back");
        const restoredFrontImageUrl = restoredFrontImageReference
          ? getPublicImageUrl(restoredFrontImageReference)
          : undefined;
        const restoredBackImageUrl = restoredBackImageReference
          ? getPublicImageUrl(restoredBackImageReference)
          : undefined;
        const frontDesignSpec = imageReferencesMatch(
          renderSnapshots?.front?.imageReference,
          restoredFrontImageReference
        )
          ? parseDesignSpecSnapshot(renderSnapshots?.front?.designSpec)
          : null;
        const backDesignSpec = imageReferencesMatch(
          renderSnapshots?.back?.imageReference,
          restoredBackImageReference
        )
          ? parseDesignSpecSnapshot(renderSnapshots?.back?.designSpec)
          : null;
        restoreImageState({
          formData: mergedFormData,
          imageUrl: restoredFrontImageUrl,
          imagePath:
            getGeneratedImageStoragePath(restoredFrontImageUrl) ?? undefined,
          frontDesignSpec,
          backImageUrl: restoredBackImageUrl,
          backImagePath:
            getGeneratedImageStoragePath(restoredBackImageUrl) ?? undefined,
          backDesignSpec,
          ticketId,
        });

        const restoredPrompt =
          typeof safeFormState.prompt === "string"
            ? safeFormState.prompt.trim()
            : "";
        if (
          restoredPrompt &&
          !restoredFrontImageUrl &&
          !restoredBackImageUrl &&
          !hasRenderableChatTranscript(restoredMessages)
        ) {
          // Hidden capability turns and blank interrupted-stream placeholders
          // are model context, not a visible customer workspace. Preserve the
          // meaningful saved prompt as an explicit manual retry; never replay a
          // restored turn automatically.
          setPendingComposerSeed(restoredPrompt);
        }
      }

      setSessionVersions({
        sessionVersion: typeof sessionVersion === "number" ? sessionVersion : 0,
        messageVersion:
          typeof messageVersion === "number"
            ? messageVersion
            : Array.isArray(messages)
              ? messages.length
              : 0,
      });

      return true;
    };

    /**
     * Fetches a session to restore, with the same HONEST three-way answer the
     * server's own ownership read uses.
     *
     * Collapsing every failure into "nothing here" is what let a transient 500
     * (or a rate-limit 429) look identical to a definitively absent row — and
     * the caller's response to "absent" was to throw the id away, which is the
     * conversation fork this work exists to eliminate, just relocated to the
     * browser. A 404 is definitively absent; the server's typed 409 is a
     * definitive archived project that must be restored from My Dresses first.
     */
    const fetchRestorableSession = async (
      sessionId: string | null
    ): Promise<
      | { status: "found"; data: { sessionId: string } }
      | { status: "absent" }
      | { status: "archived" }
      | { status: "deleted" }
      | { status: "error" }
    > => {
      const query = sessionId
        ? `?sessionId=${encodeURIComponent(sessionId)}`
        : "";
      const controller = new AbortController();
      let deadlineReleased = false;
      let didTimeOut = false;
      let timeoutId: number | null = null;
      let releaseDeadline!: () => void;
      const deadline = new Promise<null>(resolve => {
        releaseDeadline = () => {
          if (deadlineReleased) return;
          deadlineReleased = true;
          resolve(null);
        };
      });
      const cancelRestore = () => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }
        controller.abort();
        releaseDeadline();
      };
      activeRestoreCancels.add(cancelRestore);
      timeoutId = window.setTimeout(() => {
        didTimeOut = true;
        cancelRestore();
      }, AGENT_SESSION_RESTORE_TIMEOUT_MS);

      try {
        // Keep the deadline around the entire response, not only the headers.
        // `fetch()` resolves before a streamed body has necessarily completed;
        // an unbounded `response.json()` would otherwise hold the entry shell
        // forever after the timeout timer had already been cleared.
        const request = (async () => {
          const response = await fetch(`/api/create/agent/session${query}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            signal: controller.signal,
          });

          const result = await response.json().catch(() => null);
          if (!response.ok) {
            if (response.status === 409) {
              if (result?.details?.code === "AGENT_SESSION_ARCHIVED") {
                return { status: "archived" } as const;
              }
              if (result?.details?.code === "AGENT_SESSION_DELETED") {
                return { status: "deleted" } as const;
              }
            }
            return response.status === 404
              ? ({ status: "absent" } as const)
              : ({ status: "error" } as const);
          }

          if (result?.success && isAgentSessionId(result?.data?.sessionId)) {
            return { status: "found", data: result.data } as const;
          }

          // A successful envelope with the wrong payload shape is not proof
          // that no dress exists. Treat it as a transport/server-contract
          // failure so it can never replace the live canvas with undefined
          // session state.
          return { status: "error" } as const;
        })().catch(error => {
          // Timeout and effect teardown deliberately abort the transport. Turn
          // either into the deadline result so the losing request cannot create
          // an unhandled rejection after Promise.race has settled.
          if (controller.signal.aborted) return null;
          throw error;
        });

        const result = await Promise.race([request, deadline]);
        if (result) return result;

        if (didTimeOut && !disposed) {
          logger.warn("Agent-session restore timed out", {
            component: "useAgentSession",
            metadata: {
              sessionId,
              timeoutMs: AGENT_SESSION_RESTORE_TIMEOUT_MS,
            },
          });
        }
        return { status: "error" };
      } finally {
        activeRestoreCancels.delete(cancelRestore);
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      }
    };

    const initializeSession = async () => {
      if (
        !enabled ||
        freshStartRequested ||
        authoritativeRestoreTargetKey ||
        hasAuthoritativeCreateRestoreInCurrentUrl()
      ) {
        settleRestoration();
        return;
      }
      if (requestedSessionId && !isAgentSessionId(requestedSessionId)) {
        const message =
          "This dress link is invalid. Return to My Dresses and try again.";
        setAgentError(message);
        setExactSessionRestoreError(message);
        settleRestoration();
        return;
      }
      const currentSessionId = useCreatePageStore.getState().agent.sessionId;
      // A signed-out visitor keeps the id THEY minted: the conversation it
      // names survives sign-in, which is when the row is finally created under
      // that same id. An id left behind by a customer who signed out is a
      // different thing entirely — on a shared browser the next visitor's
      // anonymous turns would be attributed to that customer's conversation —
      // so it is dropped.
      if (!userId || (requestedSessionId && isAnonymous)) {
        // Only once auth has actually settled: mid-hydration `session` is null
        // for everyone, and dropping the id there would throw away a signed-in
        // customer's live conversation on every page load.
        if (!authLoading && isStoredAgentSessionAuthenticated()) {
          clearStoredAgentSessionId();
        }
        if (!authLoading) {
          if (requestedSessionId) {
            const message = "Sign in to open this saved dress.";
            setAgentError(message);
            setExactSessionRestoreError(message);
          }
          settleRestoration();
        }
        return;
      }

      // Existing-account sign-in normally transfers the anonymous chat row.
      // If the server explicitly reports that the atomic adoption failed, the
      // old local id is not owned by this account. Keep the transcript restored
      // from the save point, but let the next turn mint a fresh owned session.
      if (
        !failedAdoptionHandledRef.current &&
        typeof window !== "undefined" &&
        new URL(window.location.href).searchParams.get("_anon_work_adopted") ===
          "0"
      ) {
        failedAdoptionHandledRef.current = true;
        clearStoredAgentSessionId();
        if (currentSessionId) {
          setSessionId(null);
        }
        settleRestoration();
        return;
      }

      // Prevent duplicate initialization
      if (initializingGenerationRef.current === restoreGeneration) {
        return;
      }

      initializingGenerationRef.current = restoreGeneration;

      try {
        // My Dresses links name the exact owned session to restore. They take
        // precedence over this browser's localStorage so opening an older
        // cross-device draft cannot silently land in the most recent chat.
        if (requestedSessionId) {
          const restored = await fetchRestorableSession(requestedSessionId);
          if (restored.status === "found") {
            // Only replace the canvas and durable pointer after the server has
            // proven this exact session exists and belongs to this account.
            const didRestore = applyRestoredSession(restored.data, {
              allowSessionSwitch: true,
            });
            if (didRestore) {
              setExactSessionRestoreError(null);
              markStoredAgentSessionAuthenticated();
            }
          } else if (restored.status === "absent") {
            const message =
              "We couldn't find that saved dress. It may have been deleted.";
            setAgentError(message);
            setExactSessionRestoreError(message);
          } else if (restored.status === "archived") {
            const message =
              "This dress is archived. Restore it from My Dresses before continuing.";
            setAgentError(message);
            setExactSessionRestoreError(message);
          } else if (restored.status === "deleted") {
            const message =
              "This dress was permanently deleted. Return to My Dresses to choose another dress.";
            setAgentError(message);
            setExactSessionRestoreError(message);
          } else {
            const message = EXACT_SESSION_RESTORE_RETRY_MESSAGE;
            setAgentError(message);
            setExactSessionRestoreError(message);
          }
          return;
        }

        if (currentSessionId) {
          if (!isAgentSessionId(currentSessionId)) {
            clearStoredAgentSessionId();
            logger.debug("Skipped persisting invalid agent sessionId", {
              component: "useAgentSession",
              metadata: { sessionId: currentSessionId },
            });
            return;
          }
          storeAgentSessionId(currentSessionId);
        }

        const localSessionId = isAgentSessionId(currentSessionId)
          ? currentSessionId
          : readStoredAgentSessionId();

        try {
          if (localSessionId) {
            markStoredAgentSessionAuthenticated();
            const restored = await fetchRestorableSession(localSessionId);
            if (restored.status === "found") {
              applyRestoredSession(restored.data);
              return;
            }
            if (
              restored.status === "archived" ||
              restored.status === "deleted"
            ) {
              // My Dresses can retire the locally open project from another
              // route, tab, or device. Drop both the durable pointer and the
              // matching canvas so the next first turn mints a genuinely new
              // id instead of retrying a server-enforced 409 forever. The
              // reset marker also prevents an older dress from unexpectedly
              // replacing the fresh entry surface.
              const currentStore = useCreatePageStore.getState();
              currentStore.abortActiveRequest();
              markAgentSessionReset();
              FormPersistenceService.clearCanonicalState();
              resetAll();
              logger.info("Retired blocked local dress pointer", {
                component: "useAgentSession",
                metadata: {
                  sessionId: localSessionId,
                  reason: restored.status,
                },
              });
              return;
            }
            // The id is kept either way. Under client-minted ids a 404 is the
            // NORMAL state of a conversation whose first turn has not been
            // persisted yet (every anonymous conversation, and any turn whose
            // persistence stood down) — throwing it away there would break the
            // anonymous-to-signed-in continuity it exists for. A failed read
            // says nothing at all. Neither is a reason to go looking for a
            // DIFFERENT conversation, so the fallback is skipped too.
            logger.debug("Stored session not restored this load", {
              component: "useAgentSession",
              metadata: {
                storedSessionId: localSessionId,
                reason: restored.status,
              },
            });
            return;
          }

          // A recorded Start Over means the customer ended their conversation
          // on purpose. "No id" then means "no conversation", not "this browser
          // lost its id" — restoring here would silently undo the reset.
          if (wasAgentSessionReset()) {
            return;
          }

          if (hasAttemptedServerFallbackRef.current) {
            return;
          }
          hasAttemptedServerFallbackRef.current = true;

          const fallback = await fetchRestorableSession(null);
          if (fallback.status === "found") {
            const didRestore = applyRestoredSession(fallback.data);
            if (didRestore) {
              markStoredAgentSessionAuthenticated();
              logger.info("Restored most recent server session", {
                component: "useAgentSession",
                metadata: { sessionId: fallback.data.sessionId },
              });
            }
          }
        } catch (restoreError) {
          // Nothing is created on failure: the next send mints a fresh id.
          logger.warn("Failed to restore agent session", {
            component: "useAgentSession",
            metadata: {
              error:
                restoreError instanceof Error
                  ? restoreError.message
                  : String(restoreError),
            },
          });
        }
      } catch (error) {
        // Log error but don't block UI - chat works without session
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "Unknown error";

        logger.warn(
          "Failed to initialize agent session (chat will continue without session)",
          {
            component: "useAgentSession",
            metadata: {
              error: errorMessage,
              hasSession: Boolean(userId),
              userId: userId || null,
            },
          }
        );
      } finally {
        if (initializingGenerationRef.current === restoreGeneration) {
          initializingGenerationRef.current = null;
        }
        settleRestoration();
      }
    };

    void initializeSession();

    return () => {
      disposed = true;
      activeRestoreCancels.forEach(cancelRestore => cancelRestore());
      activeRestoreCancels.clear();
    };
    // Re-run only when auth/session identity changes or local sync state changes.
  }, [
    enabled,
    userId,
    authLoading,
    isAnonymous,
    setSessionId,
    restoreMessages,
    setPendingComposerSeed,
    setPhase,
    setAgentError,
    setExactSessionRestoreError,
    setAgentBrainState,
    updateFormData,
    restoreImageState,
    setSessionVersions,
    resetAll,
    authoritativeRestoreTargetKey,
    requestedSessionId,
    freshStartRequested,
  ]);

  return restorationPending;
}
